import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Fab from '@mui/material/Fab';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { Link } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient.js';
import SEO from './SEO.js';

// Lazy load components for performance
const ProductBox = lazy(() => import('./ProductBox.js'));
const ProductSkeleton = lazy(() => import('./ProductSkeleton.js'));

const Home = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Triggers new fetch
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  const PRODUCTS_PER_BATCH = 12;

  // --- MAIN DATA FETCHER (RANDOM) ---
  const loadProducts = useCallback(async () => {
    try {
      // Call the Database Function "get_random_products"
      const { data, error } = await supabase
        .rpc('get_random_products', { count: PRODUCTS_PER_BATCH });

      if (error) throw error;

      if (data && data.length > 0) {
        // Map DB columns to App props
        const mappedProducts = data.map(p => ({
            ...p,
            name: p.title,              
            imageUrls: p.image_urls || [], 
            keywords: p.keywords || []
        }));

        // Append new random items to the existing list
        setAllProducts(prev => [...prev, ...mappedProducts]);
      } else {
        // Stop if DB returns nothing (rare for random)
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setHasMore(false);
    }
  }, [refreshTrigger]);

  // Initial Load
  useEffect(() => {
    loadProducts();
  }, []); // Run once on mount

  // Scroll to Top Logic
  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) setShowScrollTop(true);
      else setShowScrollTop(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="container mt-5">
      <SEO />
      
      {/* --- CATEGORY TABS --- */}
      <div className="d-flex gap-2 overflow-auto mb-4 pb-2">
        <Link to="/category" className="btn btn-dark rounded-pill text-nowrap">
            <strong>All Categories</strong>
        </Link>
        <Link to="/category/Electronics" className="btn btn-outline-primary rounded-pill text-nowrap">Electronics</Link>
        <Link to="/category/Industrial" className="btn btn-outline-primary rounded-pill text-nowrap">Industrial</Link>
        <Link to="/category/Tools" className="btn btn-outline-primary rounded-pill text-nowrap">Tools</Link>
        <Link to="/category/Safety" className="btn btn-outline-primary rounded-pill text-nowrap">Safety</Link>
        <Link to="/category" className="btn btn-dark rounded-pill text-nowrap">
            <strong>All Brands</strong>
        </Link>
      </div>

      <h1 className="mb-4">Featured Products</h1>
      
      <InfiniteScroll
        dataLength={allProducts.length}
        next={() => setRefreshTrigger(prev => prev + 1)} // Load more random items
        hasMore={hasMore}
        loader={
          <div className="row mt-3">
            {[...Array(4)].map((_, i) => (
              <Suspense key={i} fallback={<Skeleton height={200} />}>
                <ProductSkeleton />
              </Suspense>
            ))}
          </div>
        }
      >
        <div className="row">
          {allProducts.map((product, index) => (
            // Use index in key to prevent React errors if random DB returns duplicates
            <Suspense key={`${product.id}-${index}`} fallback={<Skeleton height={200} />}>
              <ProductBox product={product} />
            </Suspense>
          ))}
        </div>
      </InfiniteScroll>

      {showScrollTop && (
        <Fab color="primary" size="small" onClick={scrollToTop} style={{ position: 'fixed', bottom: 16, right: 16 }}>
          <KeyboardDoubleArrowUpIcon />
        </Fab>
      )}
    </div>
  );
};

export default Home;